import { z } from "zod";
import { findInternalVoice, describeInternalVoice } from "@/utils/displayCopyGuard";
import { httpUrlOrBlankSchema, programWebsiteSchema } from "./linkFieldSchemas";

/**
 * KB-2665 — wrap a family-facing copy field so internal engineering voice cannot be saved into it.
 * One helper, applied to every note the session page displays, so the rule has exactly one home.
 */
const displayableCopy = <T extends z.ZodType<string | undefined>>(schema: T) =>
  schema.superRefine((value, ctx) => {
    const findings = findInternalVoice(value);
    if (findings.length > 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: describeInternalVoice(findings) });
    }
  });

// Create a time validation helper function that assigns errors to specific fields
const validateTimeSequence = (data: any) => {
  // Helper to check if time A is before or equal to time B (when both exist)
  // Returns the field name that should show the error, or null if valid
  const checkTimeOrder = (timeA: number | null, timeB: number | null, fieldNameA: string, fieldNameB: string) => {
    if (timeA === null || timeB === null) return null;
    if (timeA > timeB) {
      return fieldNameA; // The first time is causing the problem
    }
    return null;
  };
  
  // No validation errors by default
  const errors: Record<string, string> = {};
  
  // Check within each row (start must be before end)
  if (checkTimeOrder(data.beforeCareStart, data.beforeCareEnd, "beforeCareStart", "beforeCareEnd")) {
    errors["beforeCareStart"] = "Start cannot be after end time";
  }
  
  if (checkTimeOrder(data.regularDropOffStart, data.regularDropOffEnd, "regularDropOffStart", "regularDropOffEnd")) {
    errors["regularDropOffStart"] = "Start cannot be after end time";
  }
  
  if (checkTimeOrder(data.regularProgrammingStart, data.regularProgrammingEnd, "regularProgrammingStart", "regularProgrammingEnd")) {
    errors["regularProgrammingStart"] = "Start cannot be after end time";
  }
  
  if (checkTimeOrder(data.regularPickUpStart, data.regularPickUpEnd, "regularPickUpStart", "regularPickUpEnd")) {
    errors["regularPickUpStart"] = "Start cannot be after end time";
  }
  
  if (checkTimeOrder(data.afterCareStart, data.afterCareEnd, "afterCareStart", "afterCareEnd")) {
    errors["afterCareStart"] = "Start cannot be after end time";
  }
  
  // Check sequence between rows
  if (data.beforeCareEnd !== null && data.regularDropOffStart !== null) {
    if (data.beforeCareEnd > data.regularDropOffStart) {
      errors["beforeCareEnd"] = "Cannot be after drop-off start";
    }
  }
  
  if (data.regularDropOffEnd !== null && data.regularProgrammingStart !== null) {
    if (data.regularDropOffEnd > data.regularProgrammingStart) {
      errors["regularDropOffEnd"] = "Cannot be after programming start";
    }
  }
  
  if (data.regularProgrammingEnd !== null && data.regularPickUpStart !== null) {
    if (data.regularProgrammingEnd > data.regularPickUpStart) {
      errors["regularProgrammingEnd"] = "Cannot be after pick-up start";
    }
  }
  
  if (data.regularPickUpEnd !== null && data.afterCareStart !== null) {
    if (data.regularPickUpEnd > data.afterCareStart) {
      errors["regularPickUpEnd"] = "Cannot be after after-care start";
    }
  }
  
  return errors;
};

const timingsSchema = z.object({
  regularDropOffStart: z.number().nullable().optional(),
  regularDropOffEnd: z.number().nullable().optional(),
  regularProgrammingStart: z.number().nullable().optional(),
  regularProgrammingEnd: z.number().nullable().optional(),
  regularPickUpStart: z.number().nullable().optional(),
  regularPickUpEnd: z.number().nullable().optional(),
  beforeCareStart: z.number().nullable().optional(),
  beforeCareEnd: z.number().nullable().optional(),
  afterCareStart: z.number().nullable().optional(),
  afterCareEnd: z.number().nullable().optional(),
  dropOffStart: z.number().nullable().optional(),
  dropOffEnd: z.number().nullable().optional(),
  pickUpStart: z.number().nullable().optional(),
  pickUpEnd: z.number().nullable().optional(),
  timingNote: z.string().optional(),
  // New optional day fields for overnight camps
  dropOffDay: z.string().optional(),
  pickUpDay: z.string().optional()
}).superRefine((data, ctx) => {
  const errors = validateTimeSequence(data);
  
  // Add each error to its specific field
  Object.entries(errors).forEach(([field, message]) => {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: message,
      path: [field]
    });
  });
});

// Alternative pricing schema
const regularOfferIncrementAlternativeSchema = z.object({
  key: z.string()
    .min(1, "Length selection is required")
    .regex(/^oi_/, "Must be a valid offer increment key"),
  price: z.number()
    .positive("Price must be greater than 0")
    .max(99999, "Price cannot exceed $99,999"),
  priceTier2: z.number()
    .positive("Tier 2 price must be greater than 0")
    .max(99999, "Tier 2 price cannot exceed $99,999")
    .nullable()
    .optional(),
  priceTier3: z.number()
    .positive("Tier 3 price must be greater than 0")
    .max(99999, "Tier 3 price cannot exceed $99,999")
    .nullable()
    .optional(),
  // KB-2665: shown to families on the session page, so it is held to family-facing copy.
  note: displayableCopy(
    z.string().max(200, "Note cannot exceed 200 characters"),
  )
    .optional()
    .default("")
});

const pricingTiersSchema = z.object({
  v: z.literal(1),
  numTiers: z.union([z.literal(2), z.literal(3)]),
  tier1Label: z.string().trim().max(60).nullable().optional(),
  tier2Label: z.string().trim().max(60).nullable().optional(),
  tier3Label: z.string().trim().max(60).nullable().optional(),
}).superRefine((val, ctx) => {
  const t1 = (val.tier1Label ?? "").trim();
  const t2 = (val.tier2Label ?? "").trim();
  const t3 = (val.tier3Label ?? "").trim();

  // Requirement: if numTiers >= 2, require tier1 AND tier2 labels
  if (val.numTiers >= 2) {
    if (t1.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["tier1Label"],
        message: "Tier 1 label is required when using multiple price tiers."
      });
    }
    if (t2.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["tier2Label"],
        message: "Tier 2 label is required when using multiple price tiers."
      });
    }
  }

  // If numTiers == 3, require tier3 label. If numTiers == 2, disallow a tier3 label.
  if (val.numTiers === 3) {
    if (t3.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["tier3Label"],
        message: "Tier 3 label is required when using 3 price tiers."
      });
    }
  } else if (val.numTiers === 2) {
    if (t3.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["tier3Label"],
        message: "Tier 3 label is only valid when using 3 price tiers."
      });
    }
  }
});

export const ageGroupVariationSchema = z.object({
  key: z.string()
       .min(1, "ID is required")
       .max(40, "ID cannot exceed 40 characters")
       .regex(/^[a-zA-Z0-9_-]+$/, "ID can only contain letters, numbers, underscores, and hyphens"),
  visibility: z.string().optional(),
  displayTitle: z.string().min(1, "Program name is required"),
  description: z.string().max(2000).optional(),
  eligibilityNotes: z.string().optional(),
  // KB-4351 — legacy "Information URL": no input any more, carried through so a save never drops it.
  url: z.string().optional(),
  programUrl: programWebsiteSchema,
  defaultSessionUrl: httpUrlOrBlankSchema,
  regUrl: httpUrlOrBlankSchema,
  dataFetchUrl: z.string().optional(),
  defaultSessionDescription: z.string().optional(),
  minAgeInYears: z.number().nullable().optional(),
  maxAgeInYears: z.number().nullable().optional(),
  minAgeNote: z.string().optional(),
  maxAgeNote: z.string().optional(),
  minAgeGradeKey: z.string().optional(),
  maxAgeGradeKey: z.string().optional(),
  minAgeGradeValue: z.number().nullable().optional(),
  maxAgeGradeValue: z.number().nullable().optional(),
  showMinAgeGradeAsPrimary: z.boolean().optional(),
  showMaxAgeGradeAsPrimary: z.boolean().optional(),
  offeringTypeKeys: z.array(z.string()).optional(),
  regularPrice: z.number().nullable().optional(),
  priceRegular: z.number().nullable().optional(),
  priceRegularTier2: z.number().nullable().optional(),
  priceRegularTier3: z.number().nullable().optional(),
  // KB-2665: family-facing, same rule as the per-length alternative note below.
  priceNote: displayableCopy(z.string()).optional(),
  pricingTiers: pricingTiersSchema.nullable().optional(),
  // KB-4181 — the program's declared price state; null = Priced. Declared here or zod strips it on save.
  priceState: z.enum(["free", "unknown"]).nullable().optional(),
  regularOfferIncrementKey: z.string().optional(),
  numDays: z.number().nullable().optional(),
  timings: timingsSchema.optional(),
  
  // NEW: Allow session-level age overrides
  allowSessionAgeOverride: z.boolean().optional().default(false),
  
  // Alternative pricing system - NEW
  regularOfferIncrementAlternatives: z.array(regularOfferIncrementAlternativeSchema)
    .optional()
    .default([])
    .refine(alternatives => {
      if (!alternatives || alternatives.length === 0) return true;
      const keys = alternatives.map(alt => alt.key);
      return new Set(keys).size === keys.length;
    }, "Each length option can only be used once")
});

export type AgeGroupVariationFormData = z.infer<typeof ageGroupVariationSchema>;
